import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Search, X } from "lucide-react";
import { useSearchIndex } from "./SearchIndexContext";
import { searchHits } from "./SearchIndex";
import { SearchResults } from "./SearchPanel";

export function HeaderSearch({ transparent = false }: { transparent?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const index = useSearchIndex();
  const results = useMemo(() => searchHits(index, query), [index, query]);

  useEffect(() => {
    setOpen(false);
    setQuery("");
  }, [pathname]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  useEffect(() => setActiveIdx(0), [query]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!open) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const hit = results[activeIdx];
      if (hit) {
        e.preventDefault();
        navigate({ to: hit.to as never, params: hit.params as never });
        setOpen(false);
        setQuery("");
      }
    }
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Search"
        className={
          "inline-flex h-9 w-9 items-center justify-center rounded-full transition " +
          (transparent
            ? "text-white hover:bg-white/10"
            : "text-primary/80 hover:bg-primary/5 hover:text-primary")
        }
      >
        <Search className="h-4.5 w-4.5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-[min(92vw,28rem)]">
          <div className="rounded-xl border border-border bg-white shadow-xl">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
              <Search className="h-4 w-4 text-primary/70" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search…"
                className="min-w-0 flex-1 bg-transparent text-base text-primary placeholder:text-muted-foreground/70 focus:outline-none"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    inputRef.current?.focus();
                  }}
                  aria-label="Clear"
                  className="grid h-6 w-6 place-items-center rounded-full text-muted-foreground hover:bg-primary/10"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {query.trim() && (
              <SearchResults
                results={results}
                activeIdx={activeIdx}
                setActiveIdx={setActiveIdx}
                onSelect={() => setOpen(false)}
                query={query}
                className="border-0 shadow-none rounded-xl rounded-b-2xl"
              />
            )}
            {!query.trim() && (
              <div className="px-4 py-3 text-sm text-muted-foreground">
                Try "audit", "transfer pricing", "dividend tax"…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
